import { reportUsageHeartbeat } from '@/api/usage'

const HEARTBEAT_INTERVAL_MS = 60 * 1000
const ACTIVE_WINDOW_MS = 5 * 60 * 1000
const TRACKED_PAGES = {
  '/project': 'PROJECT',
  '/writing/step1': 'WRITING_STEP1',
  '/writing/step2': 'WRITING_STEP2',
  '/writing/novelty-search': 'NOVELTY_SEARCH',
}

let initialized = false
let heartbeatTimer = null
let routerRef = null
let removeAfterEachHook = null
let lastActiveAt = Date.now()
let lastReportAt = Date.now()
let currentContext = buildDefaultContext()

export function useUsageHeartbeat(router) {
  if (initialized || !router) {
    return
  }

  initialized = true
  routerRef = router
  syncContext(router.currentRoute.value)
  markActive()
  bindActivityEvents()

  heartbeatTimer = window.setInterval(() => {
    flushHeartbeat()
  }, HEARTBEAT_INTERVAL_MS)

  removeAfterEachHook = router.afterEach((to) => {
    flushHeartbeat({ keepalive: true })
    syncContext(to)
    markActive()
  })
}

function buildDefaultContext() {
  return {
    routePath: '',
    pageCode: '',
    pageTitle: '',
    projectId: '',
    sessionId: getSessionId(),
    tabId: getTabId(),
  }
}

function bindActivityEvents() {
  ;['click', 'keydown', 'scroll', 'mousemove'].forEach((eventName) => {
    window.addEventListener(eventName, markActive, { passive: true })
  })

  document.addEventListener('visibilitychange', handleVisibilityChange)
  window.addEventListener('pagehide', handlePageHide)
  window.addEventListener('beforeunload', handlePageHide)
}

function handleVisibilityChange() {
  if (document.visibilityState === 'hidden') {
    flushHeartbeat({ keepalive: true, allowHidden: true })
    return
  }
  markActive()
}

function handlePageHide() {
  flushHeartbeat({ keepalive: true, allowHidden: true })
}

function markActive() {
  lastActiveAt = Date.now()
}

function syncContext(route) {
  currentContext = {
    routePath: route?.path || '',
    pageCode: getPageCode(route?.path),
    pageTitle: document.title || route?.meta?.title || '',
    projectId: getProjectId(route),
    sessionId: currentContext.sessionId || getSessionId(),
    tabId: currentContext.tabId || getTabId(),
  }
  lastReportAt = Date.now()
}

function getProjectId(route) {
  const raw = route?.query?.id || route?.query?.projectId || ''
  return raw ? String(raw) : ''
}

function getPageCode(path) {
  return TRACKED_PAGES[path] || ''
}

function getToken() {
  return localStorage.getItem('token')
}

function isTrackedPage() {
  return !!currentContext.pageCode
}

function isActive(now) {
  return now - lastActiveAt <= ACTIVE_WINDOW_MS
}

function flushHeartbeat(options = {}) {
  if (!routerRef || !getToken() || !isTrackedPage()) {
    return Promise.resolve(false)
  }

  const now = Date.now()
  const visible = document.visibilityState === 'visible'
  const active = isActive(now)
  const elapsedSeconds = Math.floor((now - lastReportAt) / 1000)
  const countable = active && (visible || options.allowHidden)
  const activeSeconds = countable ? Math.min(Math.max(elapsedSeconds, 0), 60) : 0

  if (activeSeconds <= 0) {
    return Promise.resolve(false)
  }

  lastReportAt = now

  return reportUsageHeartbeat({
    heartbeatId: generateId(),
    sessionId: currentContext.sessionId,
    tabId: currentContext.tabId,
    routePath: currentContext.routePath,
    pageCode: currentContext.pageCode,
    pageTitle: currentContext.pageTitle,
    projectId: currentContext.projectId,
    visible,
    active,
    activeSeconds,
    clientReportTime: now,
    lastActiveTime: lastActiveAt,
    terminalType: 'pc',
  }, {
    keepalive: Boolean(options.keepalive),
  })
}

function getSessionId() {
  const storageKey = 'usage-heartbeat-session-id'
  let sessionId = sessionStorage.getItem(storageKey)
  if (!sessionId) {
    sessionId = generateId()
    sessionStorage.setItem(storageKey, sessionId)
  }
  return sessionId
}

function getTabId() {
  const storageKey = 'usage-heartbeat-tab-id'
  let tabId = sessionStorage.getItem(storageKey)
  if (!tabId) {
    tabId = generateId()
    sessionStorage.setItem(storageKey, tabId)
  }
  return tabId
}

function generateId() {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID()
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`
}

export function stopUsageHeartbeat() {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer)
    heartbeatTimer = null
  }
  if (removeAfterEachHook) {
    removeAfterEachHook()
    removeAfterEachHook = null
  }
  initialized = false
}
